export class DamageEstimation {
    static costEstimationKeys = ['labour_time', 'paint_time', 'strip_assembly', 'paint_materials'];
    static userTrackedKeys = {
        labour_time: 'labour_time_user',
        paint_time: 'paint_time_user',
        strip_assembly: 'strip_assembly_user',
        paint_materials: 'paint_materials_user'
    };
    estimation;
    isAutomaticDetection;
    Price;
    operation;
    piece = undefined;
    paint = {
        time: undefined,
        cost: undefined,
        materials: undefined
    };
    labour = {
        time: undefined,
        cost: undefined
    };
    assembly = {
        time: undefined,
        cost: undefined
    };
    description;
    value;
    cost;
    total;
    provider;
    reference;
    Estimation;
    constructor(estimation) {
        this.Estimation = estimation;
        this.estimation = { ...this.Estimation };
        this.set(this.estimation);
        this.value = this.estimation.value;
        const { description } = this.estimation;
        const isAdditional = ['discount', 'excess', 'consumable'].includes(this.operation ?? '');
        this.isAutomaticDetection = !DamageEstimation.hasManualEstimation(estimation);
        if (isAdditional) {
            this.description = description;
        }
        this.calculate();
    }
    static hasManualEstimation(estimation) {
        const userCostEstimationKeys = DamageEstimation.costEstimationKeys
            .map((key) => DamageEstimation.userTrackedKeys[key])
            .filter((key) => key !== undefined);
        return userCostEstimationKeys.some((key) => (estimation[key] ?? undefined) !== undefined);
    }
    static filterPrice(originalPrice, userPrice) {
        if (originalPrice === userPrice) {
            return undefined;
        }
        return userPrice;
    }
    hasManualEstimationChanges() {
        return DamageEstimation.costEstimationKeys.some((key) => {
            const userTrackedKey = DamageEstimation.userTrackedKeys[key];
            if (!userTrackedKey) {
                return false;
            }
            if (!this.estimation[userTrackedKey]) {
                return false;
            }
            return this.estimation[key] !== this.estimation[userTrackedKey];
        });
    }
    set(estimation) {
        const { operation, operation_user: operationUser } = estimation;
        const { part_price: partPrice, part_price_user: partPriceUser, part_provider: partProvider, part_provider_user: partProviderUser, part_reference: partReference, part_reference_user: partReferenceUser } = estimation;
        const { paint_time: paintTime, paint_time_user: paintTimeUser, paint_materials: paintMaterials, paint_materials_user: paintMaterialsUser } = estimation;
        const { strip_assembly: stripAssembly, strip_assembly_user: stripAssemblyUser } = estimation;
        const { labour_time: labourTime, labour_time_user: labourTimeUser } = estimation;
        const piecePrice = partPriceUser ?? partPrice;
        this.description = estimation.description;
        this.operation = operationUser ?? operation;
        this.piece = this.operation === 'replace' ? piecePrice : undefined;
        this.paint.time = paintTimeUser ?? paintTime;
        this.paint.materials = paintMaterialsUser ?? paintMaterials;
        this.assembly.time = stripAssemblyUser ?? stripAssembly;
        this.labour.time = labourTimeUser ?? labourTime;
        this.provider = partProviderUser ?? partProvider;
        this.reference = partReferenceUser ?? partReference;
        this.value = estimation.value;
    }
    calculate() {
        if (!this.Price) {
            return;
        }
        const { paint, labour } = this.Price;
        this.paint.cost = (this.paint.time ?? 0) * paint;
        this.labour.cost = (this.labour.time ?? 0) * labour;
        this.assembly.cost = (this.assembly.time ?? 0) * labour;
        this.total = (this.piece ?? 0) + (this.paint.materials ?? 0) + this.paint.cost + this.labour.cost + this.assembly.cost;
    }
    updatePrice({ labour, paint }) {
        this.Price = { labour, paint };
        this.calculate();
    }
    getPrice() {
        if (!this.Price) {
            return undefined;
        }
        return { ...this.Price };
    }
    getIsAutomaticDetection() {
        return this.isAutomaticDetection;
    }
    update(estimation, isManualUpdate = true) {
        this.estimation.description = estimation.description;
        this.estimation.operation_user = estimation.operation;
        this.estimation.value = estimation.value;
        if (!isManualUpdate) {
            this.estimation.part_price = estimation.part_price;
            this.estimation.part_provider = estimation.part_provider;
            this.estimation.part_reference = estimation.part_reference;
            this.estimation.paint_time = estimation.paint_time;
            this.estimation.paint_materials = estimation.paint_materials;
            this.estimation.labour_time = estimation.labour_time;
            this.estimation.strip_assembly = estimation.strip_assembly;
        }
        this.estimation.part_price_user = estimation.part_price;
        this.estimation.part_provider_user = estimation.part_provider;
        this.estimation.part_reference_user = estimation.part_reference;
        this.estimation.paint_time_user = estimation.paint_time;
        this.estimation.paint_materials_user = estimation.paint_materials;
        this.estimation.labour_time_user = estimation.labour_time;
        this.estimation.strip_assembly_user = estimation.strip_assembly;
        if (isManualUpdate && this.hasManualEstimationChanges()) {
            this.isAutomaticDetection = false;
        }
        this.set(this.estimation);
        this.calculate();
    }
    export(isIADamage, isNewDamage = false) {
        if (!isNewDamage) {
            return {
                description: this.description,
                operation: this.operation,
                part_price: this.piece,
                part_reference: this.reference,
                part_provider: this.provider,
                paint_time: this.paint.time,
                paint_materials: this.paint.materials,
                labour_time: this.labour.time,
                strip_assembly: this.assembly.time,
                value: this.value,
                is_automatic_detection: this.isAutomaticDetection && isIADamage ? true : undefined
            };
        }
        return {
            description: this.description,
            operation: this.operation,
            part_price: this.estimation.operation === 'replace' ? this.estimation.part_price : undefined,
            part_price_user: this.estimation.operation_user === 'replace'
                ? DamageEstimation.filterPrice(this.estimation.part_price, this.estimation.part_price_user)
                : undefined,
            part_reference: this.reference,
            part_provider: this.provider,
            paint_time: this.estimation.paint_time,
            paint_time_user: DamageEstimation.filterPrice(this.estimation.paint_time, this.estimation.paint_time_user),
            paint_materials: this.estimation.paint_materials,
            paint_materials_user: DamageEstimation.filterPrice(this.estimation.paint_materials, this.estimation.paint_materials_user),
            labour_time: this.estimation.labour_time,
            labour_time_user: DamageEstimation.filterPrice(this.estimation.labour_time, this.estimation.labour_time_user),
            strip_assembly: this.estimation.strip_assembly,
            strip_assembly_user: DamageEstimation.filterPrice(this.estimation.strip_assembly, this.estimation.strip_assembly_user),
            value: this.value,
            is_automatic_detection: this.isAutomaticDetection && isIADamage ? true : undefined
        };
    }
    addCost(cost) {
        this.cost = cost;
    }
}
